import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Trash, FloppyDisk, Check, CaretDown, Tag, Barbell } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_GROUP_KEYS = [
  "petto", "schiena", "spalle", "bicipiti", "tricipiti",
  "quadricipiti", "femorali", "glutei", "polpacci", "core",
];

const DEFAULT_GROUP_LABELS = {
  petto: "Petto", schiena: "Schiena", spalle: "Spalle", bicipiti: "Bicipiti",
  tricipiti: "Tricipiti", quadricipiti: "Quadricipiti", femorali: "Femorali",
  glutei: "Glutei", polpacci: "Polpacci", core: "Core",
};

export default function MuscleMappingsPanel() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [extraSyn, setExtraSyn] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [countSecondary, setCountSecondary] = useState(true);
  const [secondaryWeight, setSecondaryWeight] = useState(0.5);
  const [newSyn, setNewSyn] = useState("");
  const [newSynTarget, setNewSynTarget] = useState("schiena");
  const [newGroupKey, setNewGroupKey] = useState("");
  const [newGroupLabel, setNewGroupLabel] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/muscle-mappings`);
      setExtraSyn(data?.extra_synonyms || []);
      setCustomGroups(data?.custom_groups || []);
      if (typeof data?.count_secondary === "boolean") setCountSecondary(data.count_secondary);
      if (typeof data?.secondary_weight === "number") setSecondaryWeight(data.secondary_weight);
    } catch (err) {
      console.warn("fetchMuscleMappings failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const payload = {
        extra_synonyms: patch.extra_synonyms ?? extraSyn,
        custom_groups: patch.custom_groups ?? customGroups,
        count_secondary: patch.count_secondary ?? countSecondary,
        secondary_weight: patch.secondary_weight ?? secondaryWeight,
      };
      const { data } = await axios.put(`${API}/settings/muscle-mappings`, payload);
      setExtraSyn(data?.extra_synonyms || []);
      setCustomGroups(data?.custom_groups || []);
      if (typeof data?.count_secondary === "boolean") setCountSecondary(data.count_secondary);
      if (typeof data?.secondary_weight === "number") setSecondaryWeight(data.secondary_weight);
      setSavedAt(Date.now());
    } catch (err) {
      console.warn("saveMuscleMappings failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const availableTargets = [
    ...DEFAULT_GROUP_KEYS.map((k) => ({ key: k, label: DEFAULT_GROUP_LABELS[k] })),
    ...customGroups.map((g) => ({ key: g.key, label: g.label })),
  ];

  const addSynonym = async () => {
    const syn = newSyn.trim().toLowerCase();
    const tgt = newSynTarget.trim().toLowerCase();
    if (!syn || !tgt) return;
    const next = [...extraSyn.filter((s) => s.synonym !== syn), { synonym: syn, target: tgt }];
    await save({ extra_synonyms: next });
    setNewSyn("");
  };

  const removeSynonym = async (syn) => {
    const next = extraSyn.filter((s) => s.synonym !== syn);
    await save({ extra_synonyms: next });
  };

  const addCustomGroup = async () => {
    const key = newGroupKey.trim().toLowerCase().replace(/\s+/g, "_");
    const label = newGroupLabel.trim();
    if (!key || !label) return;
    if (DEFAULT_GROUP_KEYS.includes(key) || customGroups.some((g) => g.key === key)) return;
    const next = [...customGroups, { key, label }];
    await save({ custom_groups: next });
    setNewGroupKey("");
    setNewGroupLabel("");
  };

  const removeCustomGroup = async (key) => {
    const next = customGroups.filter((g) => g.key !== key);
    // Also clean any synonym pointing to it
    const nextSyn = extraSyn.filter((s) => s.target !== key);
    await save({ custom_groups: next, extra_synonyms: nextSyn });
  };

  const toggleCountSecondary = async () => {
    const next = !countSecondary;
    setCountSecondary(next);
    await save({ count_secondary: next });
  };

  const changeSecondaryWeight = async (v) => {
    setSecondaryWeight(v);
    await save({ secondary_weight: v });
  };

  return (
    <div className="bg-[#0F0F0F] border border-[#262626] rounded-xl overflow-hidden" data-testid="muscle-mappings-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#1A1A1A]"
        data-testid="muscle-mappings-toggle"
      >
        <div className="w-9 h-9 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/30 flex items-center justify-center flex-shrink-0">
          <Tag size={16} weight="fill" className="text-[#D4FF00]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Mappatura gruppi muscolari</p>
          <p className="text-[11px] text-zinc-500 truncate">
            {extraSyn.length} sinonimi custom · {customGroups.length} gruppi custom
          </p>
        </div>
        <CaretDown size={14} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="p-4 pt-2 border-t border-[#1A1A1A] space-y-5" data-testid="muscle-mappings-body">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="w-5 h-5 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* ---- Secondary muscles ---- */}
          <section>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Barbell size={11} /> Muscoli secondari
            </h4>
            <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2.5 py-2 mb-2">
              <div className="min-w-0 pr-2">
                <p className="text-xs text-white font-medium leading-tight">Conteggio serie indirette</p>
                <p className="text-[10px] text-zinc-600 leading-tight mt-0.5">
                  Somma le serie anche al muscolo secondario dell&apos;esercizio
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={countSecondary}
                onClick={toggleCountSecondary}
                disabled={saving}
                className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors flex-shrink-0 ${
                  countSecondary ? "bg-[#D4FF00]" : "bg-[#262626]"
                }`}
                data-testid="secondary-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    countSecondary ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
            {countSecondary && (
              <div className="flex items-center gap-1.5" data-testid="secondary-weight-row">
                <span className="text-[10px] text-zinc-500 font-medium mr-1">Peso</span>
                {[0.25, 0.5, 0.75, 1].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => changeSecondaryWeight(v)}
                    disabled={saving}
                    className={`px-2 py-1 text-[10px] font-bold rounded-md border transition-colors ${
                      Math.abs(secondaryWeight - v) < 0.001
                        ? "bg-[#007AFF] border-[#007AFF] text-white"
                        : "bg-[#0A0A0A] border-[#262626] text-zinc-400 hover:text-white"
                    }`}
                    data-testid={`secondary-weight-${String(v).replace(".", "-")}`}
                  >
                    {v}x
                  </button>
                ))}
                <span className="text-[10px] text-zinc-600 ml-auto">
                  1 serie → {secondaryWeight} sul secondario
                </span>
              </div>
            )}
          </section>

          {/* ---- Custom groups ---- */}
          <section>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Barbell size={11} /> Gruppi personalizzati
            </h4>
            {customGroups.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {customGroups.map((g) => (
                  <span
                    key={g.key}
                    className="inline-flex items-center gap-1 bg-[#D4FF00]/10 border border-[#D4FF00]/30 text-[#D4FF00] text-[11px] font-semibold px-2 py-1 rounded-full"
                    data-testid={`custom-group-${g.key}`}
                  >
                    {g.label}
                    <button
                      type="button"
                      onClick={() => removeCustomGroup(g.key)}
                      className="ml-1 text-[#D4FF00]/60 hover:text-[#FF3B30]"
                      aria-label={`Rimuovi ${g.label}`}
                      data-testid={`custom-group-remove-${g.key}`}
                    >
                      <Trash size={10} weight="fill" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <label className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold">Etichetta</label>
                <input
                  type="text"
                  value={newGroupLabel}
                  onChange={(e) => setNewGroupLabel(e.target.value)}
                  placeholder="Avambracci"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#D4FF00]"
                  data-testid="custom-group-label"
                />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold">Chiave (no spazi)</label>
                <input
                  type="text"
                  value={newGroupKey}
                  onChange={(e) => setNewGroupKey(e.target.value.replace(/\s+/g, "_"))}
                  placeholder="avambracci"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#D4FF00]"
                  data-testid="custom-group-key"
                />
              </div>
              <button
                type="button"
                onClick={addCustomGroup}
                disabled={!newGroupKey || !newGroupLabel || saving}
                className="px-3 py-1.5 bg-[#D4FF00] text-[#0A0A0A] rounded-md font-bold text-xs disabled:opacity-40"
                data-testid="custom-group-add"
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
          </section>

          {/* ---- Synonyms ---- */}
          <section>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Tag size={11} /> Sinonimi
              <span className="text-zinc-600 normal-case font-normal tracking-normal text-[10px]">
                (rimappa una parola già usata nella libreria a un gruppo)
              </span>
            </h4>
            {extraSyn.length > 0 && (
              <ul className="mb-3 space-y-1">
                {extraSyn.map((s) => (
                  <li
                    key={s.synonym}
                    className="flex items-center gap-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2.5 py-1.5"
                    data-testid={`synonym-${s.synonym}`}
                  >
                    <span className="text-xs text-white font-medium">{s.synonym}</span>
                    <span className="text-[10px] text-zinc-600">→</span>
                    <span className="text-[11px] font-bold text-[#007AFF]">
                      {availableTargets.find((t) => t.key === s.target)?.label || s.target}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSynonym(s.synonym)}
                      className="ml-auto text-zinc-500 hover:text-[#FF3B30]"
                      aria-label="Rimuovi"
                      data-testid={`synonym-remove-${s.synonym}`}
                    >
                      <Trash size={11} weight="fill" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <label className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold">Parola</label>
                <input
                  type="text"
                  value={newSyn}
                  onChange={(e) => setNewSyn(e.target.value)}
                  placeholder="trapezio"
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#007AFF]"
                  data-testid="synonym-input"
                />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold">Gruppo</label>
                <select
                  value={newSynTarget}
                  onChange={(e) => setNewSynTarget(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#007AFF]"
                  data-testid="synonym-target"
                >
                  {availableTargets.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={addSynonym}
                disabled={!newSyn || saving}
                className="px-3 py-1.5 bg-[#007AFF] text-white rounded-md font-bold text-xs disabled:opacity-40"
                data-testid="synonym-add"
              >
                {saving ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Plus size={14} weight="bold" />
                )}
              </button>
            </div>
          </section>

          {savedAt > 0 && Date.now() - savedAt < 2000 && (
            <p className="text-[10px] text-[#34C759] flex items-center gap-1 font-medium" data-testid="muscle-mappings-saved">
              <Check size={11} weight="bold" /> Salvato
            </p>
          )}
        </div>
      )}
    </div>
  );
}
